import { useCallback, useEffect, useRef, useState } from 'react';
import { buildCompetitionUrl } from '@/api/competitionApi';

export type AntiCheatEventType = 'tab-hidden' | 'window-blur';

interface UseAntiCheatOptions {
  enabled: boolean;
}

interface UseAntiCheatReturn {
  showAlert: boolean;
  dismissAlert: () => void;
}

export function useAntiCheat({ enabled }: UseAntiCheatOptions): UseAntiCheatReturn {
  const [showAlert, setShowAlert] = useState(false);
  const lastSentAtRef = useRef<number>(0);
  const alertVisibleRef = useRef(false);

  const dismissAlert = useCallback(() => {
    setShowAlert(false);
    alertVisibleRef.current = false;
  }, []);

  useEffect(() => {
    function sendEvent(type: AntiCheatEventType): void {
      const payload = JSON.stringify({ type });
      const blob = new Blob([payload], { type: 'application/json' });
      const endpoint = buildCompetitionUrl('/api/v1/focus-event');

      fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {
        navigator.sendBeacon(endpoint, blob);
      });
    }

    function recordInfraction(type: AntiCheatEventType): void {
      if (!enabled) return;

      const now = Date.now();
      const DEBOUNCE_MS = 500;
      const isDebounced = now - lastSentAtRef.current < DEBOUNCE_MS;

      if (!isDebounced) {
        lastSentAtRef.current = now;
        sendEvent(type);
      }

      if (!alertVisibleRef.current) {
        alertVisibleRef.current = true;
        setShowAlert(true);
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        recordInfraction('tab-hidden');
      }
    }

    function handleWindowBlur(): void {
      recordInfraction('window-blur');
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [enabled]);

  return { showAlert, dismissAlert };
}
